import React, { useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const HOURS_IN_DAY = 24;
const HOUR_LABELS = [0, 3, 6, 9, 12, 15, 18, 21];

function getTimeOfDay(isoString) {
  const date = new Date(isoString);
  return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
}

function formatHour(hour) {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
}

function Timeline({
  recordings,
  selectedDate,
  currentTime,
  currentRecording,
  onSeekToTime,
  onRecordingClick,
}) {
  const timelineRef = useRef(null);

  const handleClick = (e) => {
    if (!timelineRef.current || !onSeekToTime) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const hour = percentage * HOURS_IN_DAY;

    // Find a recording at this time
    const recording = recordings.find((r) => {
      const start = getTimeOfDay(r.time);
      const end = getTimeOfDay(r.end);
      return hour >= start && hour <= end;
    });

    if (recording) {
      onRecordingClick?.(recording);
    }
  };

  // Calculate current playback position marker
  let currentPositionPercent = null;
  if (currentRecording && currentTime !== undefined) {
    const recordingStart = getTimeOfDay(currentRecording.time);
    const recordingDuration = (currentRecording.duration || 0) / 3600; // in hours
    const currentHour = recordingStart + (currentTime / 3600);
    currentPositionPercent = (currentHour / HOURS_IN_DAY) * 100;
  }

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Hour labels */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, px: 0.5 }}>
        {HOUR_LABELS.map((hour) => (
          <Typography key={hour} variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {formatHour(hour)}
          </Typography>
        ))}
      </Box>

      {/* Timeline bar */}
      <Box
        ref={timelineRef}
        onClick={handleClick}
        sx={{
          position: 'relative',
          height: 24,
          bgcolor: 'grey.200',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {/* Recording segments */}
        {recordings.map((recording) => {
          const startHour = getTimeOfDay(recording.time);
          const endHour = getTimeOfDay(recording.end);
          const left = (startHour / HOURS_IN_DAY) * 100;
          const width = ((endHour - startHour) / HOURS_IN_DAY) * 100;

          const isSelected = currentRecording?.filename === recording.filename;
          const startTime = new Date(recording.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const endTime = new Date(recording.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

          return (
            <Tooltip key={recording.filename} title={`${startTime} - ${endTime}`}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  bottom: 2,
                  left: `${left}%`,
                  width: `${Math.max(width, 0.5)}%`,
                  bgcolor: isSelected ? 'primary.main' : 'success.main',
                  borderRadius: 0.5,
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : 'success.dark',
                  },
                }}
              />
            </Tooltip>
          );
        })}

        {/* Current position marker */}
        {currentPositionPercent !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${currentPositionPercent}%`,
              width: 2,
              bgcolor: 'error.main',
              zIndex: 1,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default Timeline;
